import React, { useState, useEffect, useCallback } from 'react';
import { getVarieties, getGrowthPhasesByVariety, createGrowthPhase, updateGrowthPhase, deleteGrowthPhase } from '../../services/v2/adminService';
import TableHeader from '../../shared/components/tableHeader/TableHeader';

const GrowthPhaseAdmin = () => {
    const [varieties, setVarieties] = useState([]);
    const [selectedVariety, setSelectedVariety] = useState('');
    const [phases, setPhases] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const emptyFormData = {
        sequenceOrder: '',
        phaseName: '',
        completionMetric: 'GDD',
        targetGdd: '',
        gddTolerance: '',
        targetChill: '',
        targetDli: '',
        dliTolerance: '',
        expectedDays: '',
        daysTolerance: ''
    };

    const [formData, setFormData] = useState(emptyFormData);

    const TABLE_HEADERS = ['Orden', 'Nombre Etapa', 'Métrica', 'Objetivo', 'DLI Obj/día (Tol)', 'Días Esp (Tol)', 'Acciones'];

    const fetchPhases = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await getGrowthPhasesByVariety(selectedVariety);
            setPhases(data || []);
        } catch (error) {
            console.error('Error fetching phases', error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedVariety]);
    
    useEffect(() => {
        const loadVarieties = async () => {
            const data = await getVarieties();
            setVarieties(data || []);
        };
        loadVarieties();
    }, []);

    useEffect(() => {
        if (selectedVariety) {
            fetchPhases();
        } else {
            setPhases([]);
        }
    }, [selectedVariety, fetchPhases]);

    

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await updateGrowthPhase(editingId, selectedVariety, formData);
            } else {
                await createGrowthPhase(selectedVariety, formData);
            }
            setFormData(emptyFormData);
            setEditingId(null);
            fetchPhases();
        } catch (error) {
            console.error('Error saving phase', error);
        }
    };

    const handleEdit = (phase) => {
        setEditingId(phase.id);
        setFormData({
            sequenceOrder: phase.sequenceOrder ?? '',
            phaseName: phase.phaseName ?? '',
            completionMetric: phase.completionMetric || 'GDD',
            targetGdd: phase.targetGdd ?? '',
            gddTolerance: phase.gddTolerance ?? '',
            targetChill: phase.targetChill ?? '',
            targetDli: phase.targetDli ?? '',
            dliTolerance: phase.dliTolerance ?? '',
            expectedDays: phase.expectedDays ?? '',
            daysTolerance: phase.daysTolerance ?? ''
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData(emptyFormData);
    };

    const handleDelete = async (phaseId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta etapa?')) return;
        try {
            await deleteGrowthPhase(phaseId);
            fetchPhases();
        } catch (error) {
            console.error('Error deleting phase', error);
        }
    };

    const inputClasses = "w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm";

    return (
        <div className="flex flex-col">
            <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
                <h2 className="text-xl font-bold text-gray-800">Definir Etapas por Variedad</h2>
                
                <div className="w-full md:w-64">
                    <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Seleccionar Variedad</label>
                    <select 
                        value={selectedVariety} 
                        onChange={(e) => setSelectedVariety(e.target.value)}
                        className={inputClasses}
                    >
                        <option value="">-- Elija una variedad --</option>
                        {varieties.map(v => <option key={v.id} value={v.id}>{v.name} ({v.cropType?.name})</option>)}
                    </select>
                </div>
            </div>
            
            {selectedVariety ? (
                <>
                    {/* Inline Form to add phases */}
                    <form onSubmit={handleSubmit} className="mb-8 bg-blue-50 p-6 rounded-xl border border-blue-100 shadow-sm">
                        <h3 className="text-sm font-bold text-blue-800 mb-4 flex items-center">
                            <span className="material-icons-outlined mr-2">{editingId ? 'edit' : 'add_circle'}</span>
                            {editingId ? 'Editar Etapa Fenológica' : 'Añadir Nueva Etapa Fenológica'}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
                            <div className="lg:col-span-1">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Orden</label>
                                <input type="number" name="sequenceOrder" placeholder="Ej: 1" value={formData.sequenceOrder} onChange={handleChange} required className={inputClasses} />
                            </div>
                            <div className="lg:col-span-2">
                                <label className="block text-xs font-medium text-gray-500 mb-1">Nombre de la Etapa</label>
                                <input type="text" name="phaseName" placeholder="Ej: Vegetativo, Floración, Dormancia..." value={formData.phaseName} onChange={handleChange} required className={inputClasses} />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Se mide por</label>
                                <select name="completionMetric" value={formData.completionMetric} onChange={handleChange} required className={inputClasses}>
                                    <option value="GDD">GDD (grados día)</option>
                                    <option value="CHILL">Frío acumulado (Chill Portions)</option>
                                    <option value="DAYS">Días fijos</option>
                                    <option value="MANUAL">Manual (el usuario cierra la etapa)</option>
                                </select>
                            </div>

                            {formData.completionMetric === 'GDD' && (
                                <>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">GDD Obj.</label>
                                        <input type="number" step="0.1" name="targetGdd" placeholder="Ej: 350" value={formData.targetGdd} onChange={handleChange} required className={inputClasses} />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Tolerancia GDD</label>
                                        <input type="number" step="0.1" name="gddTolerance" placeholder="Ej: 50" value={formData.gddTolerance} onChange={handleChange} required className={inputClasses} />
                                    </div>
                                </>
                            )}

                            {formData.completionMetric === 'CHILL' && (
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Chill Portions Obj.</label>
                                    <input type="number" step="0.1" name="targetChill" placeholder="Ej: 45" value={formData.targetChill} onChange={handleChange} required className={inputClasses} />
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">DLI Obj. promedio diario (opcional)</label>
                                <input type="number" step="0.1" name="targetDli" placeholder="Ej: 15 (mol/m²/día)" value={formData.targetDli} onChange={handleChange} className={inputClasses} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Tolerancia DLI</label>
                                <input type="number" step="0.1" name="dliTolerance" placeholder="Ej: 2" value={formData.dliTolerance} onChange={handleChange} className={inputClasses} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Días Esp.</label>
                                <input type="number" name="expectedDays" placeholder="Ej: 15" value={formData.expectedDays} onChange={handleChange} required className={inputClasses} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Tolerancia Días</label>
                                <input type="number" name="daysTolerance" placeholder="Ej: 3" value={formData.daysTolerance} onChange={handleChange} required className={inputClasses} />
                            </div>
                            
                            <div className="md:col-span-3 lg:col-span-4 flex justify-end gap-2">
                                {editingId && (
                                    <button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        className="px-6 py-2 bg-gray-200 text-gray-700 font-bold rounded-md hover:bg-gray-300 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                                >
                                    {editingId ? 'Guardar Cambios' : 'Agregar Etapa'}
                                </button>
                            </div>
                        </div>
                    </form>

                    {isLoading ? (
                        <div className="text-center py-10 text-gray-500">Cargando etapas...</div>
                    ) : (
                        <div className="relative overflow-x-auto shadow-sm border border-gray-200 rounded-lg">
                            <table className="w-full text-sm text-left text-gray-500">
                                <TableHeader headerOptions={TABLE_HEADERS} />
                                <tbody className="divide-y divide-gray-200">
                                    {phases.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-4 text-center text-gray-400">No hay etapas definidas para esta variedad</td>
                                        </tr>
                                    ) : (
                                        phases.sort((a,b) => a.sequenceOrder - b.sequenceOrder).map(p => {
                                            const metric = p.completionMetric || 'GDD';
                                            const METRIC_LABELS = { GDD: 'GDD', CHILL: 'Frío', DAYS: 'Días', MANUAL: 'Manual' };
                                            return (
                                            <tr key={p.id} className="bg-white hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 font-bold text-blue-600">
                                                    {p.sequenceOrder}
                                                </td>
                                                <th scope="row" className="px-6 py-4 font-bold text-gray-900 whitespace-nowrap">
                                                    {p.phaseName}
                                                </th>
                                                <td className="px-6 py-4">
                                                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                                        {METRIC_LABELS[metric] || metric}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-orange-600 font-medium">
                                                    {metric === 'CHILL'
                                                        ? <>{p.targetChill} <span className="text-xs text-gray-400">Chill Portions</span></>
                                                        : metric === 'GDD'
                                                            ? <>{p.targetGdd} <span className="text-xs text-gray-400">(±{p.gddTolerance || 0}) GDD</span></>
                                                            : <span className="text-xs text-gray-400">—</span>}
                                                </td>
                                                <td className="px-6 py-4 text-yellow-600 font-medium">
                                                    {p.targetDli ?? '—'} <span className="text-xs text-gray-400">(±{p.dliTolerance || 0})</span>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600">
                                                    {p.expectedDays} <span className="text-xs text-gray-400">(±{p.daysTolerance || 0})</span>
                                                </td>
                                                <td className="px-6 py-4 flex gap-1">
                                                    <button
                                                        onClick={() => handleEdit(p)}
                                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                                    >
                                                        <span className="material-icons-outlined">edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(p.id)}
                                                        className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                                    >
                                                        <span className="material-icons-outlined">delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        );})
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-20 text-center">
                    <span className="material-icons-outlined text-5xl text-gray-300 mb-4">info</span>
                    <p className="text-gray-500 font-medium">Seleccione una variedad arriba para gestionar sus etapas fenológicas.</p>
                </div>
            )}
        </div>
    );
};

export default GrowthPhaseAdmin;
